import photoSwipeCompatibility from './built-in-plugins/photoSwipeCompatibility';
import initOnlineOffline from './built-in-plugins/onlineOffline';
import firefoxScrolltopHotfix from './built-in-plugins/firefoxScrolltopHotfix';
import './jqueryMigrate';
import './jqueryTransit';

/**
 * This is the API that should be visible to customer-specific Asteikko Mag
 * IssueReader customizations, such as UI renderers and
 * issue page template -specific JS code (e.g. code that listens to hooks,
 * such as "reader_opened" and "reader_page_shown").
 *
 * Issue reader customizations should never access the AppApi directly. They
 * should only access the IssueReaderPluginApi, since it provides a stable API.
 */
export default class IssueReaderPluginApi {

  constructor( app ) {
    this._app = app;

    photoSwipeCompatibility( this );
    firefoxScrolltopHotfix( this );
    initOnlineOffline( this );

    // Since this object is the API that customer-specific customizations should
    // use, we expose the API as a global variable
    window.AsteikkoReaderApi = this;
  }

  get appName() {
    return this._app.appName;
  }

  get env() {
    return this._app.env;
  }

  get networkStatus() {
    return this._app.networkStatus;
  }

  get reader() {
    return this._app.issueReader;
  }

  get issue() {
    return this._app.issueReader.issue;
  }

  get pages() {
    return this._app.issueReader.issue ? this._app.issueReader.issue.pages : null;
  }

  get currentPage() {
    return this.pages ? this.pages[ this.currentPageIndex ] : null;
  }

  get currentSpreadPages() {
    return this.reader.currentSpreadPages;
  }

  get currentPageIndex() {
    return this.reader.pageIndex;
  }

  get i18n() {
    return this._app.i18n;
  }

  get user() {
    return this._app.user;
  }

  get social() {
    return this._app.social;
  }

  get imageUtils() {
    return this._app.utils.img;
  }

  log( ...args ) {
    this._app.console.log.apply( this._app.console, args );
  }

  warn( ...args ) {
    this._app.console.warn.apply( this._app.console, args );
  }

  warning( ...args ) {
    return this.warn.apply( this, args );
  }

  error( ...args ) {
    this._app.console.error.apply( this._app.console, args );
  }

  /**
   * See https://www.npmjs.com/package/@wordpress/hooks for documentation on
   * these methods.
   */

  addFilter( hookName, namespace, callback, priority ) {
    return this._app.hooks.addFilter( hookName, namespace, callback, priority );
  }

  addAction( hookName, namespace, callback, priority ) {
    return this._app.hooks.addAction( hookName, namespace, callback, priority );
  }

  removeAction( hookName, namespace ) {
    return this._app.hooks.removeAction( hookName, namespace );
  }

  removeFilter( hookName, namespace ) {
    return this._app.hooks.removeFilter( hookName, namespace );
  }

  doAction( hookName, ...args ) {
    const allArgs = [ hookName ].concat( args );

    return this._app.hooks.doAction.apply( this._app.hooks, allArgs );
  }

  applyFilters( hookName, content, ...args ) {
    const allArgs = [ hookName, content ].concat( args );

    return this._app.hooks.applyFilters.apply( this._app.hooks, allArgs );
  }

  doingAction( hookName ) {
    return this._app.hooks.doingAction( hookName );
  }

  doingFilter( hookName ) {
    return this._app.hooks.doingFilter( hookName );
  }

  didAction( hookName ) {
    return this._app.hooks.didAction( hookName );
  }

  didFilter( hookName ) {
    return this._app.hooks.didFilter( hookName );
  }

  hasAction( hookName, namespace ) {
    return this._app.hooks.hasAction( hookName, namespace );
  }

  hasFilter( hookName, namespace ) {
    return this._app.hooks.hasFilter( hookName, namespace );
  }

  currentAction() {
    return this._app.hooks.currentAction();
  }

  currentFilter() {
    return this._app.hooks.currentFilter();
  }
}
